import React from "react";

import { QNA } from "@components/component-library/Atoms/FAQAccordionGroup/FAQAccordionGroup";

export const FAQs: { [key: string]: QNA[] } = {
  artist: [
    {
      question: "Does the scheme only apply to living artists?",
      answer: (
        <div className="b2">
          <p>
            No. The scheme applies to the works of living artists, and artists who have died, if it is less than 70
            years from the end of the year in which the artist died. Royalties are payable to artists and to their
            beneficiaries. The entitlement to a royalty could pass to more than one beneficiary during that 70 year
            period. For example, it could pass initially to the artist’s widower, and then to the artist’s children. The
            artist and their beneficiary or estate (successor in title) must have the required connection to Australia
            or a prescribed country.
          </p>
          <p>
            If we collect a resale royalty, we use our best efforts to identify, find and pay the artist or beneficiary.
            We encourage artists and beneficiaries to register their contact and payment details with us, so that we can
            easily find and pay them if we collect a royalty.
          </p>
        </div>
      ),
    },
    {
      question: "Is an artist’s beneficiary entitled to claim a resale royalty?",
      answer: (
        <div className="b2">
          <p>
            An artist’s beneficiary is entitled to a resale royalty if:
            <ul>
              <li>
                the artist, immediately before their death, was an Australian citizen or permanent resident or a
                national or citizen of a prescribed country; and
              </li>
              <li>
                the beneficiary is:
                <ul>
                  <li>
                    an individual who is a citizen or permanent resident of Australia or a national or citizen of a
                    prescribed country (the &quot;residency test&quot;);
                  </li>
                  <li>
                    a charity or community body that is incorporated in and/or carries on business in Australia or a
                    reciprocating country; or
                  </li>
                  <li>a trustee for one of the above.</li>
                </ul>
              </li>
            </ul>
          </p>
        </div>
      ),
    },
    {
      question: "Will I get a royalty for overseas resales?",
      answer: (
        <div className="b2">
          <p>
            As of 31 March 2024, Australian resale right holders are entitled to resale royalties from resales of their
            works in 17 countries, including the UK, France and Germany, subject to the requirements of the schemes in
            those countries. A further list of countries was added as at 31 March 2025. Copyright Agency has agreements
            with the visual arts management organisations in all the prescribed countries and we can collect from them
            any royalties for you unless you tell us not to.
          </p>
          <p>
            Other countries will be added from time to time. Please check the Art Market Professionals information page
            for the latest list of countries.
          </p>
        </div>
      ),
    },
    {
      question: "How much money will Copyright Agency deduct from my resale royalty?",
      answer: (
        <div className="b2">
          <p>
            By arrangement with the government, from 1 July 2015, our administrative fee is up to 15% of the royalty
            collected. We are required to collect GST on our administrative fee, payable to the Government, which we
            deduct from the royalty, so that the total deduction is 16.5%.
          </p>
          <p>An artist who is registered for GST can claim that GST on their tax return.</p>
        </div>
      ),
    },
    {
      question: "What if I don’t want to receive resale royalties?",
      answer: (
        <div className="b2">
          <p>
            Each time one of your works is resold in Australia, we display information about it on this website – see
            ‘Notice of resale’ page. Once we publish that information, we wait 21 days before sending an invoice. In
            that 21 day period, you can instruct us not to collect the royalty for you. Notify us via email at{" "}
            <a href="resale@copyright.com.au">resale@copyright.com.au</a>
          </p>
        </div>
      ),
    },
    {
      question: "What if I want to collect a resale royalty in Australia myself?",
      answer: (
        <div className="b2">
          <p>
            If you want to collect a royalty in Australia yourself, you must instruct us to not to collect the royalty
            for you, within 21 days of our displaying information about the resale on this website. Provided you know
            who is responsible for paying the royalty (that is, the seller and/or the art market professional), you can
            approach them for payment of the royalty.
          </p>
          <p>
            You must notify us each time you do not want us to collect a royalty for you. Notify us via email at{" "}
            <a href="resale@copyright.com.au">resale@copyright.com.au</a>
          </p>
          <p>The legislation does not allow us to act on a notification for all future resales.</p>
        </div>
      ),
    },
    {
      question:
        "If a gallery pays me an advance against a future sale, is the sale of the artwork by the gallery a commercial resale?",
      answer: (
        <div className="b2">
          <p>
            Provided the artist still owns the work when the art market professional sells it, and the sale transfers
            ownership of the work from the artist directly to the buyer, that is not a commercial resale.
          </p>
        </div>
      ),
    },
  ],
  amp: [
    {
      question: "How does the resale royalty scheme affect me?",
      answer: (
        <div className="b2">
          <p>
            There are legal obligations for ‘art market professionals’, who include commercial galleries, auction
            houses, art dealers, operators of online art auctions and sales, and other people in the business of selling
            artworks.
          </p>
          <p>
            You need to:
            <ul>
              <li>
                make sure that your sellers know that they must report certain information about resales of artworks to
                us, or report that information yourself; and
              </li>
              <li>
                if a royalty is payable, make sure that it is paid, as you, as well as sellers, buyers and their agents,
                are liable for the royalty.
              </li>
            </ul>
          </p>
        </div>
      ),
    },
    {
      question: "Which transactions are ‘commercial resales’?",
      answer: (
        <div className="b2">
          <p>
            A transaction is a commercial resale if:
            <ul>
              <li>a person transfers ownership of an artwork to someone else in exchange for money; and</li>
              <li>that person is not the artist (or the artist’s estate); and</li>
              <li>
                it is not a private sale from one individual to another. Note: if an art market professional has been
                involved in any way it does not qualify as a private sale.
              </li>
            </ul>
          </p>
          <p>
            For information on donations under the Cultural Gifts Program, or artwork sold by a self-managed
            superannuation fund, please consult our{" "}
            <a href="https://www.resaleroyalty.org.au/COMPREHENSIVE_GUIDE_TO_ARTISTS_RESALE_ROYALTY_SCHEME_2025.pdf">
              comprehensive guide.
            </a>
          </p>
        </div>
      ),
    },
    {
      question: "Are royalties payable on artworks by artists from other countries?",
      answer: (
        <div className="b2">
          <p>
            On 31 March 2024 resale royalty entitlements expanded to include countries that have reciprocal arrangements
            with Australia. Further countries have since been added. Refer to the Art Market Professionals information
            page for the full list of countries and the date they were prescribed in legislation.
          </p>
          <p>
            This means that, from the date that countries were prescribed in legislation, the Australian scheme applies
            to artists and beneficiaries who are nationals or citizens of those prescribed countries. Likewise,
            royalties can be collected for resales of Australian artists’ works in those countries from the relevant
            dates.
          </p>
        </div>
      ),
    },
    {
      question: "Is GST payable on the 5% royalty?",
      answer: (
        <div className="b2">
          <p>
            The resale royalty is payable on the sale price, which means the amount paid for the artwork by the buyer
            including GST. However, the 5% royalty itself does not attract GST.
          </p>
        </div>
      ),
    },
    {
      question: "Who has to pay the royalty?",
      answer: (
        <div className="b2">
          <p>
            The seller, buyer and art market professional/s are jointly responsible to ensure the royalty is paid. If
            the royalty is not paid, we can take enforcement action against any one or all of these parties.
          </p>
        </div>
      ),
    },
    {
      question: "What happens if Copyright Agency cannot locate the right holder entitled to the resale royalty?",
      answer: (
        <div className="b2">
          <p>
            We maintain a database of information about artists, including their contact details and payment details.
          </p>
          <p>
            If an artist has not been conclusively identified when a resale is reported, we will presume that there is a
            right holder entitled to the royalty and use our best endeavours to identify that right holder, unless we
            have information indicating that there is no right holder who meets the residency test. If we are unable to
            identify the right holder after six years, we must refund the royalty to the person who paid it or, if that
            person cannot be found, use it for the administration of the scheme.
          </p>
        </div>
      ),
    },
    {
      question:
        "If I pay an artist an advance against a future sale, then sell the artwork, is this a commercial resale?",
      answer: (
        <div className="b2">
          <p>
            Some art market professionals give artists an advance against an expected sale in order to provide cash flow
            to the artist.
          </p>
          <p>
            Provided the artist still owns the work when the art market professional sells it, and the sale transfers
            ownership of the work from the artist directly to the buyer, that is not a commercial resale. Your
            documentation should reflect this arrangement.
          </p>
        </div>
      ),
    },
    {
      question:
        "What is my legal obligation to ensure that sellers provide accurate information about the sales history for an artwork?",
      answer: (
        <div className="b2">
          <p>
            An art market professional is legally obliged, together with the seller, to ensure the payment of any
            royalty that may be due.
          </p>
          <p>
            For any commercial resale of $1,000 (including GST) or more, a seller is required to provide us with
            sufficient information to work out if a royalty is payable. A seller may report via an agent, but must make
            sure the agent has accurate information, particularly about whether the seller owned the work on 9 June 2010
            or not.
          </p>
          <p>
            We conduct independent research to check that resales are being reported. Where appropriate, we will take
            legal action against sellers who fail to notify us of commercial resales, or provide inaccurate or
            incomplete information. An art market professional is required to provide us with a seller’s contact details
            if that is necessary for us to determine whether or not a royalty is payable on a resale.
          </p>
        </div>
      ),
    },
  ],
  artCenter: [
    {
      question: "My art centre buys and resells artworks. What do I need to do?",
      answer: (
        <div className="b2">
          <p>
            There are different implications for art centres that buy and resell artworks than for art centres who
            operate as an agent for their artists.
          </p>
          <p>
            If you buy and resell artworks, you need to:
            <ul>
              <li>report all resales over $1,000 to us; and</li>
              <li>
                pay a 5% royalty if you bought an artwork after 8 June 2010, and you resell it for $1,000 or more.
              </li>
            </ul>
          </p>
        </div>
      ),
    },
    {
      question: "When an art centre purchases an artwork from an artist do we pay the resale royalty?",
      answer: (
        <div className="b2">
          <p>No. There is no resale royalty payable on the first (or primary) sale of an artwork.</p>
          <p>
            However, we encourage art centres to report the primary sale to us. The information can help us to work out
            whether a later sale of an artwork is eligible for a resale royalty. When you log in and report, select
            ‘primary sale’ when asked ‘Did the reseller acquire this work after 8 June 2010?’
          </p>
        </div>
      ),
    },
    {
      question: "Who has the obligation to pay the royalty?",
      answer: (
        <div className="b2">
          <p>
            The art centre and the buyer are jointly liable to pay the royalty. This means that Copyright Agency can
            recover the royalty from one or both of them.
          </p>
        </div>
      ),
    },
  ],
  general: [
    {
      question: "I registered for the resale royalty scheme on the previous website, why can’t I log in now?",
      answer: (
        <div className="b2">
          <p>
            The first time you use the new system, you need to click on ‘Register’ and enter the email address you used
            in the previous system.
          </p>
          <p>
            In the new system, you will use your email address to sign in, instead of a username. The first time you
            sign in to the new resale royalty system, we’ll send you a code via email, then we’ll send an SMS to your
            mobile phone to verify your contact details.
          </p>
          <p>
            If you have any issues registering, or recalling which email address you previously used, please contact our
            Member Services team on <a href="mailto:memberservices@copyright.com.au">memberservices@copyright.com.au</a>{" "}
            or call 02 9394 7600
          </p>
        </div>
      ),
    },
    {
      question: "How do I log in to my resale royalty account?",
      answer: (
        <div className="b2">
          <p>
            Click on ‘sign in’ in the main menu. The resale royalty website requires you to sign in with your email
            address and password and then verify your identity via a code sent to your email address or mobile phone.
          </p>
        </div>
      ),
    },
    {
      question: "Can other staff in my organisation access our account?",
      answer: (
        <div className="b2">
          <p>
            Yes! In the new system, account access is via an email address, so each staff member who needs access will
            need to register using their email address.
          </p>
          <p>There are two options for registering a staff member:</p>
          <p>Option 1:</p>
          <ul className="numbered-bullets">
            <li>Staff member clicks on ‘Register’</li>
            <li>They add their email, a password and complete the multi-factor authentication process</li>
            <li>On the registration form, they select the checkbox for ‘Are you joining an existing account?’</li>
            <li>They enter the ABN of the organisation/company they work for or represent</li>
            <li>
              Copyright Agency will contact them or their organisation/company to verify they can represent the
              organisation/company
            </li>
          </ul>
          <p>Option 2:</p>
          <ul className="numbered-bullets">
            <li>Staff member calls Copyright Agency Member Services team on 02 9394 7600</li>
            <li>Copyright Agency verifies they represent the organisation/company</li>
            <li>Copyright Agency sends them an invitation to register</li>
            <li>
              Staff member clicks on ‘Register’ and uses the same email address they provided to Copyright Agency,
              enters a password and completes the multi-factor authentication process
            </li>
          </ul>
          <p>
            Please contact our Member Services team on{" "}
            <a href="mailto:memberservices@copyright.com.au">memberservices@copyright.com.au</a> or call 02 9394 7600 if
            you have any issues with either of these processes.
          </p>
        </div>
      ),
    },
    {
      question: "What is multi-factor authentication (MFA)?",
      answer: (
        <div className="b2">
          <p>
            Multi-factor authentication (MFA) is an extra layer of security that helps protect your account. It requires
            you to verify your identity in two or more ways when logging in—usually something you know (like your
            password) and something you have (like a code sent to your phone or email). This makes it much harder for
            anyone else to access your account, even if they have your password.
          </p>
          <p>
            The resale royalty website requires you to sign in with your email address and password and then verify your
            identity via a code sent to your email address or mobile phone.
          </p>
          <p>You’ll also be asked to verify your identity through MFA if you reset your password.</p>
        </div>
      ),
    },
    {
      question: "How do I change how I receive my MFA verification code (email or mobile)?",
      answer: (
        <div className="b2">
          <p>
            You can update your preference for receiving your multi-factor authentication (MFA) verification code at any
            time. To do this:
          </p>
          <p>
            <ul className="numbered-bullets">
              <li>Sign in to your account.</li>
              <li>Go to ‘My Contact Details’ in your account settings.</li>
              <li>
                Under ‘MFA Preference’, select your preferred method:
                <ul>
                  <li>SMS message – the code will be sent via SMS to your mobile phone</li>
                  <li>Email message – the code will be sent to your email address</li>
                </ul>
              </li>
              <li>Click ‘Save’ to confirm your changes.</li>
            </ul>
          </p>
          <p>
            Tip: Make sure your email address or mobile phone number is up to date on the contacts details page before
            switching your preferences.
          </p>
        </div>
      ),
    },
    {
      question: "What is the artists’ resale royalty scheme?",
      answer: (
        <div className="b2">
          <p>
            The artists&apos; resale royalty scheme started on 9 June 2010. Under the scheme:
            <ul>
              <li>resales of artworks of or over $1,000 (including GST) must be reported; and</li>
              <li>a 5% royalty is payable on some resales.</li>
            </ul>
          </p>
          <p>
            Key features of the scheme are:
            <ul>
              <li>it applies to resales of works created both before and after 9 June 2010</li>
              <li>it applies to a range of original artworks</li>
              <li>
                all resales of or over $1,000 (including GST) since 9 June 2010 must be reported, whether or not a
                royalty is payable
              </li>
              <li>
                if a work was in existence on 9 June 2010 a royalty is not payable on the first transfer of title even
                if it is a resale
              </li>
              <li>a royalty is not payable on resales for under $1,000 (including GST)</li>
              <li>
                as of 31 March 2024 the scheme has been extended to artworks created by artists from a prescribed list
                of countries.
              </li>
            </ul>
          </p>
          <p>The Australian Government has appointed the Copyright Agency to manage the scheme.</p>
          <p>
            You can find out more about the scheme in our comprehensive guide. (hyperlink to Guide to the artists’
            resale royalty scheme webpage)
          </p>
        </div>
      ),
    },
    {
      question: "What kind of artworks are covered in the scheme?",
      answer: (
        <div className="b2">
          <p>
            Artworks covered include artists’ books, batiks, carvings, ceramics, collages, digital artworks, drawings,
            engravings, fine art jewellery, glassware, installations, lithographs, multimedia artworks, paintings,
            photographs, pictures, prints, sculptures, tapestries, video artworks and weavings.
          </p>
          <p>Manuscripts, architectural drawings and plans are excluded.</p>
          <p>
            The artwork must have been:
            <ul>
              <li>created by the artist; or</li>
              <li>produced under the authority of the artist.</li>
            </ul>
          </p>
          <p>
            A limited edition reproduction, such as a numbered print, is covered if its production was overseen by the
            artist.
          </p>
          <p>Mass-produced items, such as posters, are excluded.</p>
        </div>
      ),
    },
    {
      question: "Who manages the scheme?",
      answer: (
        <div className="b2">
          <p>The Australian Government has appointed Copyright Agency to manage the scheme.</p>
          <p>
            Find out more on the{" "}
            <a href="https://www.arts.gov.au/funding-and-support/resale-royalty-scheme">Government’s website</a>,
            including a link to view the Resale Royalty for Visual Artists Act 2009 (the legislation).
          </p>
        </div>
      ),
    },
    {
      question: "How is the scheme governed?",
      answer: (
        <div className="b2">
          <p>
            Copyright Agency has been appointed by the Australian Government to manage the scheme. Copyright Agency
            reports regularly to the <a href="https://www.arts.gov.au/">Office for the Arts</a> on the operation of the
            scheme in accordance with its contract of appointment.
          </p>
          <p>
            Copyright Agency also provides its{" "}
            <a href="https://www.copyright.com.au/about-us/governance/annual-reports/">annual report</a> to the Minister
            for the Arts for tabling in Parliament.
          </p>
          <p>
            Copyright Agency is a signatory to the Code of{" "}
            <a href="https://www.copyrightcodeofconduct.org.au/">Conduct for Collecting Societies</a>, and{" "}
            <a href="https://www.copyrightcodeofconduct.org.au/annual-compliance">
              reports annually to the Code Reviewer
            </a>{" "}
            on its compliance with the Code. The Code Reviewer{" "}
            <a href="https://www.copyrightcodeofconduct.org.au/annual-compliance">invites submissions</a> on collecting
            societies’ compliance with the Code from any interested party.
          </p>
        </div>
      ),
    },
  ],
};
